import { useI18n } from '../utils/i18n';
import { useToast } from './ToastProvider';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Modal from './ui/Modal';
import { Plus, Ticket, Link } from 'lucide-react';
import ListState from './ui/ListState';
import apiService from '../services/apiService';
import TeamsMessageDetail from './TeamsMessageDetail';
import TicketCustomerField from './TicketCustomerField';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import { findCustomerIdByEmail } from '../utils/customerCache';
import { ticketPasstZurSuche } from '../utils/suchFilter';
import { useFeature } from '../utils/featuresCache';
import './TeamsMessageList.css';
import '../styles/Dashboard.css';

const TeamsMessageList = ({ limit = 30, unreadOnly = false, onMessageCountUpdate, filterSettings = {} }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [, setIsInitialLoad] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageDetail, setShowMessageDetail] = useState(false);
  const [selectedMessageDetail, setSelectedMessageDetail] = useState(null);
  const [openTickets, setOpenTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [linkedTeamsMessageIds, setLinkedTeamsMessageIds] = useState([]);
  const [ticketFilterText, setTicketFilterText] = useState('');
  const [newTicketData, setNewTicketData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: 'Teams',
    calenderMarker: null,
    assignedTo: '',
    customerId: null,
    contactId: null
  });
  const { getLocale, t } = useI18n();
  const kundenAktiv = useFeature('customers'); // #251: Platzhalter nennt Kunde nur, wenn der Kundenstamm an ist
  const { pushToast } = useToast();

  // Load current user once on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);
      } catch (err) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);

  const loadTeamsMessages = useCallback(async (isAutoRefresh = false) => {
    try {
      // Only show loading indicator on initial load, not on auto-refresh
      if (!isAutoRefresh) {
        setLoading(true);
      }
      setError(null);
      
      const response = await apiService.getTeamsMessages(limit, unreadOnly);
      let messageList = response.value || [];
      
      // Additional client-side filtering to remove messages from current user
      if (currentUser) {
        messageList = messageList.filter(message => {
          const senderName = message.from?.user?.displayName;
          const currentUserName = currentUser.displayName;
          
          // Filter out messages from current user
          if (senderName && currentUserName && senderName === currentUserName) {
            console.log(`Filtering out message from current user: ${senderName}`);
            return false;
          }
          
          return true;
        });
      }
      
      setMessages(messageList);
      
      // Callback to update parent component with message count
      if (onMessageCountUpdate) {
        onMessageCountUpdate(messageList.length);
      }
    } catch (err) {
      console.error('Error loading Teams messages:', err);
      setError(err.message || 'Fehler beim Laden der Teams-Nachrichten');
      
      // Reset count on error
      if (onMessageCountUpdate) {
        onMessageCountUpdate(0);
      }
    } finally {
      // Only hide loading indicator if it was shown
      if (!isAutoRefresh) {
        setLoading(false);
      }
      setIsInitialLoad(false);
    }
  }, [limit, unreadOnly, onMessageCountUpdate, currentUser]);

  useEffect(() => {
    // Only load messages if currentUser is available
    if (currentUser) {
      // Initial load
      loadTeamsMessages(false);
      
      // Set up auto-refresh for live updates every 60 seconds
      const intervalId = setInterval(() => {
        loadTeamsMessages(true); // Auto-refresh without loading indicator
      }, 60000);
      
      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [loadTeamsMessages, currentUser]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Relative strings per locale
    const rel = new Intl.RelativeTimeFormat(getLocale().startsWith('en') ? 'en' : 'de', { numeric: 'auto' });
    if (diffMins < 1) return getLocale().startsWith('en') ? 'just now' : 'gerade eben';
    if (diffMins < 60) return rel.format(-diffMins, 'minute');
    if (diffHours < 24) return rel.format(-diffHours, 'hour');
    if (diffDays < 7) return rel.format(-diffDays, 'day');
    
    return date.toLocaleDateString(getLocale(), { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit' 
    });
  };

  const formatMessageContent = (message) => {
    if (!message.body?.content) return getLocale().startsWith('en') ? 'Message without content' : 'Nachricht ohne Inhalt';
    
    // Remove HTML tags and CID references for display
    const content = message.body.content
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/cid:[^"'\s>]+/gi, '[Bild]') // Replace CID references with [Bild]
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    
    // Truncate long messages
    if (content.length > 100) {
      return content.substring(0, 100) + '...';
    }
    
    return content;
  };

  // Get the FULL message content without truncation for ticket storage
  const getFullMessageContent = (message) => {
    if (!message.body?.content && !message.plainTextBody) {
      return getLocale().startsWith('en') ? 'Message without content' : 'Nachricht ohne Inhalt';
    }
    
    // Priority: body.content > plainTextBody
    if (message.body?.content) {
      // Handle different body formats (same logic as TeamsMessageDetail)
      if (typeof message.body === 'string') {
        return message.body.replace(/<[^>]*>/g, '');
      } else if (typeof message.body === 'object' && message.body.content) {
        return message.body.content.replace(/<[^>]*>/g, '');
      }
    }
    
    // Fallback to plain text body
    if (message.plainTextBody) {
      return message.plainTextBody;
    }
    
    return getLocale().startsWith('en') ? 'Message without content' : 'Nachricht ohne Inhalt';
  };

  const getSenderName = (message) => {
    if (message.from?.user?.displayName) {
      return message.from.user.displayName;
    }
    return getLocale().startsWith('en') ? 'Unknown sender' : 'Unbekannter Absender';
  };

  // Filter messages based on filter settings  
  const { filteredMessages, totalFilteredCount } = useMemo(() => {
    if (!messages || messages.length === 0) return { filteredMessages: [], totalFilteredCount: 0 };
    
    let filtered = [...messages];
    
    // Filter 1: Hide read messages (if Teams messages have isRead property)
    if (filterSettings.hideReadMessages) {
      // Note: Teams messages might not have isRead property like emails do
      // This would need to be adapted based on actual Teams message structure
      filtered = filtered.filter(message => !message.isRead);
    }
    
    // Filter 2: Only messages from last X hours
    if (filterSettings.enableHoursFilter && filterSettings.hoursFilter > 0) {
      const hoursAgo = new Date();
      hoursAgo.setHours(hoursAgo.getHours() - filterSettings.hoursFilter);
      
      filtered = filtered.filter(message => {
        const messageDate = new Date(message.createdDateTime);
        return messageDate >= hoursAgo;
      });
    }
    
    // Filter 3: Keyword filtering (sender and chat topic)
    if (filterSettings.keywords && filterSettings.keywords.trim()) {
      const keywords = filterSettings.keywords
        .split('\n')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 0);
      
      if (keywords.length > 0) {
        filtered = filtered.filter(message => {
          const senderName = getSenderName(message).toLowerCase();
          const chatTopic = (message.chatTopic || '').toLowerCase();
          
          // Check if any keyword matches in sender name or chat topic
          const hasKeyword = keywords.some(keyword => 
            senderName.includes(keyword) || 
            chatTopic.includes(keyword)
          );
          
          // Filter out messages that contain keywords (blacklist approach)
          return !hasKeyword;
        });
      }
    }
    
    const totalFilteredCount = filtered.length;
    
    // Apply limit after filtering (limit prop takes precedence)
    if (limit && filtered.length > limit) {
      filtered = filtered.slice(0, limit);
    }
    
    return { filteredMessages: filtered, totalFilteredCount };
  }, [messages, filterSettings, limit]);

  // Handle right-click context menu
  const handleContextMenu = (e, message) => {
    e.preventDefault();
    setContextMenu({
      message,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleMessageClick = (message) => {
    setSelectedMessageDetail(message);
    setShowMessageDetail(true);
  };

  const handleCloseMessageDetail = () => {
    setShowMessageDetail(false);
    setSelectedMessageDetail(null);
  };

  const loadOpenTickets = async () => {
    setLoadingTickets(true);
    try {
      const [ticketsResponse, emailsResponse] = await Promise.all([
        apiService.getOpenTickets(),
        apiService.getAvailableEmails()
      ]);
      
      setOpenTickets(ticketsResponse.tickets || []);
      setAvailableEmails(emailsResponse.emails || []);
    } catch (error) {
      console.error('Error loading open tickets:', error);
      setOpenTickets([]);
      setAvailableEmails([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadLinkedItems = async () => {
    try {
      const response = await apiService.getLinkedItems();
      setLinkedTeamsMessageIds(response.linkedTeamsMessageIds || []);
    } catch (error) {
      console.error('Error loading linked items:', error);
      setLinkedTeamsMessageIds([]);
    }
  };

  // Load linked items when component mounts
  useEffect(() => {
    loadLinkedItems();
  }, []);

  const handleShowTicketModal = () => {
    if (contextMenu?.message) {
      setSelectedMessage(contextMenu.message);
      setShowTicketModal(true);
      setTicketFilterText(''); // Reset filter when opening modal
      loadOpenTickets();
    }
    handleCloseContextMenu();
  };

  // Filter tickets based on search text
  // #251: Nummer, Titel, Kunde, Ansprechpartner -- gemeinsamer Helfer
  const filteredTickets = openTickets.filter(ticket => ticketPasstZurSuche(ticket, ticketFilterText));

  const handleAddMessageToTicket = async (ticketId) => {
    if (!selectedMessage) {
      pushToast({ type: 'error', message: t('teams.errors.noneSelected', 'Fehler: Keine Teams-Nachricht ausgewählt') });
      return;
    }
    
    setModalLoading(true);
    try {
      // TODO: API-Endpoint für Teams-Message zu Ticket hinzufügen
      await apiService.addTeamsMessageToTicket(ticketId, selectedMessage.id, selectedMessage);
      setShowTicketModal(false);
      setSelectedMessage(null);
      
      // Reload linked items to update the linked symbol
      await loadLinkedItems();
      
      // Trigger ticket list refresh for correct sorting
      if (onMessageCountUpdate) {
        await onMessageCountUpdate();
      }
      // Dashboard-Ticketlisten sofort aktualisieren
      try { window.dispatchEvent(new Event('dashboardEventRefresh')); } catch (_) {}
    } catch (error) {
      console.error('Error adding Teams message to ticket:', error);
      pushToast({ type: 'error', message: t('teams.errors.addToTicket', 'Fehler beim Hinzufügen der Teams-Nachricht zum Ticket: {{details}}', { details: error.message }) });
    } finally {
      setModalLoading(false);
    }
  };

  const handleCreateNewTicket = () => {
    if (selectedMessage) {
      const fullContent = getFullMessageContent(selectedMessage);
      setNewTicketData({
        title: `Teams: ${formatMessageContent(selectedMessage).substring(0, 50)}`,
        description: fullContent, // Use full content for ticket description
        priority: 'normal',
        category: 'Teams',
        calenderMarker: null,
        assignedTo: '',
        customerId: null,
        contactId: null
      });
      // #210: per Absender-Domäne erkannten Kunden vorbelegen — fromEmail löst
      // /api/teams/messages aus den Chat-Mitgliedern auf (fehlt sie, bleibt es manuell)
      findCustomerIdByEmail(selectedMessage.fromEmail).then(cid => {
        if (cid) setNewTicketData(prev => (prev.customerId ? prev : { ...prev, customerId: cid }));
      });
    }
    setShowCreateTicketForm(true);
  };

  const handleSubmitNewTicket = async (e) => {
    e.preventDefault();
    if (!selectedMessage) {
      pushToast({ type: 'error', message: t('teams.errors.noneSelected', 'Fehler: Keine Teams-Nachricht ausgewählt') });
      return;
    }
    
    setModalLoading(true);
    try {
      // TODO: API-Endpoint für neues Ticket von Teams-Message
      await apiService.createTicketFromTeamsMessage(selectedMessage.id, newTicketData, selectedMessage);
      setShowTicketModal(false);
      setShowCreateTicketForm(false);
      setSelectedMessage(null);
      setNewTicketData({
        title: '',
        description: '',
        priority: 'normal',
        category: 'Teams',
        calenderMarker: null,
        assignedTo: '',
        customerId: null,
        contactId: null
      });
      
      // Reload linked items to update the linked symbol
      await loadLinkedItems();
      
      // Trigger ticket list refresh for correct sorting
      if (onMessageCountUpdate) {
        await onMessageCountUpdate();
      }
      // Dashboard-Ticketlisten sofort aktualisieren
      try { window.dispatchEvent(new Event('dashboardEventRefresh')); } catch (_) {}
    } catch (error) {
      console.error('Error creating ticket from Teams message:', error);
      pushToast({ type: 'error', message: t('ticket.createErrorDetails', 'Fehler beim Erstellen des Tickets: {{details}}', { details: error.message }) });
    } finally {
      setModalLoading(false);
    }
  };

  const handleCancelCreateTicket = () => {
    setShowCreateTicketForm(false);
    setNewTicketData({
      title: '',
      description: '',
      priority: 'normal',
      category: 'Teams',
      calenderMarker: null,
      assignedTo: '',
      customerId: null,
      contactId: null
    });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  if (loading) {
    return <ListState variant="loading" message={t('teams.list.loading', 'Teams-Nachrichten werden geladen…')} />;
  }

  // #256: Der Fehlerfall zeigte bisher nur einen Hinweis — ohne Weg zurueck
  // ausser die ganze Seite neu zu laden. Jetzt mit "Erneut versuchen".
  if (error) {
    return (
      <ListState
        variant="error"
        message={t('teams.list.loadFailed', 'Teams-Nachrichten konnten nicht geladen werden')}
        onRetry={() => loadTeamsMessages(false)}
      />
    );
  }

  if (messages.length === 0) {
    return (
      <div className="dashboard-empty-state">
        <div className="empty-icon">💬</div>
        <p>{unreadOnly ? 'Keine ungelesenen Teams-Nachrichten' : 'Keine Teams-Nachrichten'}</p>
      </div>
    );
  }

  if (filteredMessages.length === 0) {
    const hasActiveFilters = filterSettings.hideReadMessages || 
                           filterSettings.enableHoursFilter || 
                           (filterSettings.keywords && filterSettings.keywords.trim());
    
    return (
      <div className="dashboard-empty-state">
        <div className="empty-icon">💬</div>
        <p>{hasActiveFilters ? 'Keine Teams-Nachrichten entsprechen den aktiven Filtern' : 'Keine Teams-Nachrichten'}</p>
        {hasActiveFilters && (
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-placeholder)', marginTop: '8px' }}>
            Entfernen Sie Filter oder passen Sie die Filterkriterien an
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="dashboard-teams-list">
        {filteredMessages.map((message, index) => (
          <div 
            key={message.id || index} 
            className="teams-message-item"
            onClick={() => handleMessageClick(message)}
            onContextMenu={(e) => handleContextMenu(e, message)}
            style={{ 
              cursor: 'pointer',
              opacity: linkedTeamsMessageIds.includes(message.id) ? 0.2 : 1
            }}
          >
            <div className="message-header">
              <div className="sender-info">
                <span className="sender-name">{getSenderName(message)}</span>
                {message.chatTopic && message.chatTopic !== 'Direct Chat' && (
                  <span className="chat-topic">in {message.chatTopic}</span>
                )}
              </div>
              <div className="message-time">
                {formatDate(message.createdDateTime)}
              </div>
            </div>
            
            <div className="message-content">
              {formatMessageContent(message)}
            </div>
            
            <div className="message-footer">
              <span className="chat-type">
                {message.chatType === 'oneOnOne' ? 'Direktnachricht' : 'Gruppenchat'}
              </span>
              {message.importance && message.importance === 'high' && (
                <span className="importance-indicator">❗</span>
              )}
              {linkedTeamsMessageIds.includes(message.id) && (
                <Link size={16} style={{ marginLeft: '8px', color: 'var(--color-primary)' }} title={t('common.linkedToTicket', 'Mit Ticket verknüpft')} />
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Show indicator if more messages are available */}
      {limit && messages && totalFilteredCount > 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '10px', 
          fontSize: '0.8rem', 
          color: 'var(--color-text-muted)',
          borderTop: '1px solid var(--color-border-light)',
          background: 'var(--color-bg)'
        }}>
          {totalFilteredCount > limit 
            ? `Zeige ${limit} von ${totalFilteredCount} Teams-Nachrichten • ${totalFilteredCount - limit} weitere verfügbar`
            : `Alle ${totalFilteredCount} Teams-Nachrichten angezeigt`
          }
        </div>
      )}

      {/* CONTEXT MENU */}
      {contextMenu && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.position.y,
            left: contextMenu.position.x,
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '4px',
            boxShadow: '0 2px 10px var(--color-shadow-10)',
            padding: '8px 0',
            minWidth: '200px'
          }}
        >
          <div 
            className="context-menu-item"
            onClick={handleShowTicketModal}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-surface-hover)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <Ticket size={16} />
            {t('email.addToTicket.button', 'Zu einem Ticket hinzufügen')}
          </div>
        </div>
      )}

      {/* TICKET MODAL */}
      <Modal
        open={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedMessage(null); }}
        title={<><Link size={24} /><h3>{t('teams.addToTicket.modalTitle', 'Teams-Nachricht zu Ticket hinzufügen')}</h3>{showCreateTicketForm && (<CalenderMarkerHeaderSelect value={newTicketData.calenderMarker} onChange={(value) => setNewTicketData({...newTicketData, calenderMarker: value})} />)}</>}
        ariaLabel={t('teams.addToTicket.modalTitle', 'Teams-Nachricht zu Ticket hinzufügen')}
        maxWidth="600px"
        footer={<button type="button" onClick={() => { setShowTicketModal(false); setSelectedMessage(null); }} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>}
      >

            
            {showCreateTicketForm ? (
              <div className="create-ticket-form">
                <h4>{t('ticket.create.new', 'Neues Ticket erstellen:')}</h4>
                <form onSubmit={handleSubmitNewTicket}>
                  {/* #210: Kunde/Ansprechpartner wie in der Ticketmaske (#104);
                      Vorbelegung über die Absenderadresse aus den Chat-Mitgliedern.
                      Funktionales setState: Kundenwechsel feuert zwei Resets (#105). */}
                  <TicketCustomerField
                    value={newTicketData.customerId}
                    onChange={(customerId) => setNewTicketData(prev => ({ ...prev, customerId }))}
                    contactValue={newTicketData.contactId}
                    onContactChange={(contactId) => setNewTicketData(prev => ({ ...prev, contactId }))}
                    inputId="teams-ticket-customer"
                    contactInputId="teams-ticket-contact"
                  />
                  <div className="form-group">
                    <label>{t('form.title', 'Titel')}</label>
                    <input
                      type="text"
                      value={newTicketData.title}
                      onChange={(e) => setNewTicketData({...newTicketData, title: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>{t('form.description', 'Beschreibung')}</label>
                    <textarea
                      value={newTicketData.description}
                      onChange={(e) => setNewTicketData({...newTicketData, description: e.target.value})}
                      rows="4"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('form.priority', 'Priorität')}</label>
                      <select
                        value={newTicketData.priority}
                        onChange={(e) => setNewTicketData({...newTicketData, priority: e.target.value})}
                      >
                        <option value="niedrig">{t('priority.low', 'Niedrig')}</option>
                        <option value="normal">{t('priority.normal', 'Normal')}</option>
                        <option value="hoch">{t('priority.high', 'Hoch')}</option>
                        <option value="kritisch">{t('priority.critical', 'Kritisch')}</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>{t('form.assignedTo', 'Zugewiesen an')}</label>
                      <select
                        value={newTicketData.assignedTo}
                        onChange={(e) => setNewTicketData({...newTicketData, assignedTo: e.target.value})}
                      >
                        <option value="">{t('common.unassigned', 'Nicht zugewiesen')}</option>
                        {availableEmails.map(email => (
                          <option key={email} value={email}>{email}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  
                  
                  <div className="form-actions">
                    <button type="button" onClick={handleCancelCreateTicket} className="btn btn-secondary">
                      {t('common.back', 'Zurück')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                      {modalLoading ? t('common.creating', 'Erstelle...') : t('ticket.create.submit', 'Ticket erstellen')}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="ticket-selector">
                <h4>{t('ticket.addToExisting', 'Zu vorhandenem Ticket hinzufügen:')}</h4>
                
                {/* Filter input */}
                <div className="ticket-filter">
                  <input
                    type="text"
                    placeholder={(kundenAktiv ? t('ticket.filter.placeholderCustomer', 'Nach Ticketnummer, Titel, Kunde oder Ansprechpartner suchen...') : t('ticket.filter.placeholder', 'Nach Ticketnummer oder Titel suchen...'))}
                    value={ticketFilterText}
                    onChange={(e) => setTicketFilterText(e.target.value)}
                    className="ticket-filter-input"
                  />
                </div>
                
                {loadingTickets ? (
                  <p>{t('ticket.loading', 'Lade Tickets...')}</p>
                ) : openTickets.length === 0 ? (
                  <p>{t('ticket.noneOpen', 'Keine offenen Tickets gefunden.')}</p>
                ) : filteredTickets.length === 0 ? (
                  <p>{t('ticket.noneMatchFilter', 'Keine Tickets gefunden, die dem Filter entsprechen.')}</p>
                ) : (
                  <div className="ticket-list-scrollable">
                    {filteredTickets.map(ticket => (
                      <div key={ticket.id} className="ticket-item-compact">
                        <div className="ticket-info-compact">
                          <div className="ticket-header-compact">
                            <span className="ticket-number-compact">#{ticket.ticketNumber}</span>
                            <div className="ticket-badges-compact">
                              <span className={`status-badge-compact status-${ticket.status}`}>
                                {ticket.status}
                              </span>
                              <span className={`priority-badge-compact priority-${ticket.priority}`}>
                                {ticket.priority}
                              </span>
                            </div>
                          </div>
                          <div className="ticket-title-compact">{ticket.title}</div>
                        </div>
                        <button 
                          onClick={() => handleAddMessageToTicket(ticket.id)}
                          className="btn btn-secondary btn-compact"
                          disabled={modalLoading}
                        >
                          {modalLoading ? t('common.adding', 'Füge hinzu...') : t('common.add', 'Hinzufügen')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="create-ticket-option">
                  <button onClick={handleCreateNewTicket} className="btn btn-primary">
                    <Plus size={16} />
                    {t('ticket.create.button', 'Neues Ticket erstellen')}
                  </button>
                </div>
              </div>
            )}
      </Modal>

      {/* TEAMS MESSAGE DETAIL MODAL */}
      {showMessageDetail && (
        <TeamsMessageDetail 
          message={selectedMessageDetail}
          onClose={handleCloseMessageDetail}
        />
      )}
    </div>
  );
};

export default TeamsMessageList; 